import { useEffect, useState } from "react";
import { parseDateTimeToUtc } from "../../../utils/dateTimeHelper";

type UseDepositLimitTimerArgs = {
  currentAmount?: number | null;
  newAmount?: number | null;
  applicableOn?: string | Date | null;
  message: (amount: number, time: string) => string;
};

function formatRemainingTime(seconds: number): string {
  if (seconds <= 0) return "";

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) {
    return `${days}d ${hours.toString().padStart(2, "0")}h`;
  }

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
}

export function useDepositLimitTimer({
  currentAmount,
  newAmount,
  applicableOn,
  message,
}: UseDepositLimitTimerArgs) {
  const [amount, setAmount] = useState<string>("");
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const [note, setNote] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (currentAmount != null && newAmount != null && applicableOn != null) {
      const nowUtcMs = Date.now();

      const applyUtcMs = new Date(parseDateTimeToUtc(applicableOn)).getTime();

      if (newAmount > currentAmount && applyUtcMs > nowUtcMs) {
        setAmount(currentAmount.toString());
        setDisabled(true);
        setRemainingSeconds(Math.floor((applyUtcMs - nowUtcMs) / 1000));
        return;
      }
    }

    setAmount(currentAmount?.toString() ?? "");
    setRemainingSeconds(0);
    setNote("");
    setDisabled(Boolean(currentAmount && currentAmount > 0));
  }, [currentAmount, newAmount, applicableOn]);

  useEffect(() => {
    if (remainingSeconds <= 0) return;

    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [remainingSeconds]);

  useEffect(() => {
    if (remainingSeconds <= 0 || !newAmount) {
      setNote("");
      return;
    }

    const timeText = formatRemainingTime(remainingSeconds);
    setNote(message(newAmount, timeText));
  }, [remainingSeconds, newAmount, message]);

  return {
    amount,
    setAmount,
    note,
    disabled,
  };
}
